import { EApplicantType, EFinancingProgram, EOtherIncomeType } from '@if/core/types'
import { ActionButton, SubsectionTitle, useCan, useFormDialog } from '@if/mui'
import { List, Stack } from '@mui/material'
import { access } from '@src/access'
import { Address } from '@src/types/AddressSchema'
import {
  ApplicantJob,
  ApplicantOtherIncome,
  CreditApplication,
  JobSchema,
  OtherIncomeSchema,
} from '@src/types/CreditApplicationSchema'
import { Plus } from 'lucide-react'
import { nanoid } from 'nanoid'
import { useMemo } from 'react'
import { Control, useFieldArray, useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import DisplayJobInfosListItem from './DisplayJobInfosListItem'
import DisplayOtherIncomeListItem from './DisplayOtherIncomeListItem'
import JobDialogFields from './JobDialogFields'
import OtherIncomeDialogFields from './OtherIncomeDialogFields'

interface Props {
  formControl: Control<CreditApplication>
  applicantType: EApplicantType
  disableEdit: boolean
  toolTip: string
  financingProgramId: EFinancingProgram
}

const isAddressEmpty = (address: Address | null) =>
  Boolean(address) && Object.values(address!).every((a) => a === null || a === undefined)

const ApplicantIncomeSection = ({
  applicantType: name,
  formControl,
  disableEdit,
  toolTip,
  financingProgramId,
}: Props) => {
  const { t } = useTranslation()
  const exactMonthsUnderOneYear = useCan(access.exactMonthsUnderOneYear)
  const jobSchema = JobSchema(exactMonthsUnderOneYear, true, 'none')
  const prequalJobSchema = useMemo(() => JobSchema(exactMonthsUnderOneYear, true, 'always'), [exactMonthsUnderOneYear])

  const {
    fields: otherIncomesFields,
    append: otherIncomesAppend,
    remove: otherIncomesRemove,
    update: otherIncomesUpdate,
  } = useFieldArray({ name: `${name}.otherIncomes`, control: formControl })

  const {
    append: currentJobsAppend,
    remove: currentJobsRemove,
    update: currentJobsUpdate,
  } = useFieldArray({ name: `${name}.currentJobs`, control: formControl })

  const currentJobsFields = useWatch({ control: formControl, name: `${name}.currentJobs` })
  const { submitCount } = useFormState({ control: formControl })

  const jobDialog = useFormDialog<ApplicantJob, ApplicantJob | null>({
    title: {
      create: t('editCreditApplication.income.job.addJob'),
      edit: t('editCreditApplication.income.job.editJob'),
    },
    schema: jobSchema,
    defaultValues: (job) => job ?? { ...(jobSchema.getDefault() as unknown as ApplicantJob), id: nanoid() },
    content: ({ form, props }) => (
      <JobDialogFields
        control={form.control}
        financingProgramId={financingProgramId}
        highlightSchema={props && submitCount > 0 ? prequalJobSchema : undefined}
      />
    ),
    onSubmit: (data) => {
      const jobCopy = { ...data }
      if (isAddressEmpty(jobCopy.address)) jobCopy.address = null
      return jobCopy
    },
    successMessage: false,
    maxWidth: 'md',
  })

  const otherIncomeDialog = useFormDialog<ApplicantOtherIncome, ApplicantOtherIncome | null>({
    title: {
      create: t('editCreditApplication.income.other.addOther'),
      edit: t('editCreditApplication.income.other.editOther'),
    },
    schema: OtherIncomeSchema,
    defaultValues: (income) => income ?? ({ id: nanoid() } as ApplicantOtherIncome),
    content: ({ form }) => <OtherIncomeDialogFields control={form.control} />,
  })

  const handleAddJob = async () => {
    const saved = await jobDialog.create()
    if (saved) currentJobsAppend(saved)
  }

  const handleEditJob = async (index: number) => {
    const saved = await jobDialog.edit(currentJobsFields[index])
    if (saved) currentJobsUpdate(index, saved)
  }

  const handleAddOtherIncome = async () => {
    const saved = await otherIncomeDialog.create()
    if (saved) otherIncomesAppend(saved)
  }

  const handleEditOtherIncome = async (index: number) => {
    const saved = await otherIncomeDialog.edit(otherIncomesFields[index] as unknown as ApplicantOtherIncome)
    if (saved) otherIncomesUpdate(index, saved)
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <SubsectionTitle>{t('editCreditApplication.income.job.label')}</SubsectionTitle>
        <ActionButton id="addJob" startIcon={Plus} onClick={handleAddJob} disabled={disableEdit}>
          {t('editCreditApplication.income.job.addJob')}
        </ActionButton>
      </Stack>

      {currentJobsFields && currentJobsFields.length > 0 && (
        <List sx={{ mt: 0, py: 0 }}>
          {currentJobsFields.map((item: ApplicantJob, index: number) => (
            <DisplayJobInfosListItem
              key={item.id}
              onDelete={() => currentJobsRemove(index)}
              onEdit={() => handleEditJob(index)}
              data={item}
              error={
                !disableEdit &&
                !item.isIncomeLocked &&
                !(submitCount > 0 ? prequalJobSchema : jobSchema).isValidSync(item)
              }
              disableEdit={disableEdit || item.isIncomeLocked}
              toolTip={toolTip}
              submitCount={submitCount}
            />
          ))}
        </List>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <SubsectionTitle>{t('editCreditApplication.income.other.label')}</SubsectionTitle>
        <ActionButton id="addOtherIncomes" startIcon={Plus} onClick={handleAddOtherIncome} disabled={disableEdit}>
          {t('editCreditApplication.income.other.addOther')}
        </ActionButton>
      </Stack>

      {otherIncomesFields.some(
        (item) =>
          (item as unknown as ApplicantOtherIncome).typeId !==
          (EOtherIncomeType.IncomeAdjustmentToMatchStatements as number),
      ) && (
        <List sx={{ mt: 0, py: 0 }}>
          {otherIncomesFields.map((item, index) => {
            const income = item as unknown as ApplicantOtherIncome
            if (income.typeId === (EOtherIncomeType.IncomeAdjustmentToMatchStatements as number)) return null
            return (
              <DisplayOtherIncomeListItem
                key={item.id}
                onDelete={() => otherIncomesRemove(index)}
                onEdit={() => handleEditOtherIncome(index)}
                data={income}
                disableEdit={disableEdit || income.isIncomeLocked}
                toolTip={toolTip}
              />
            )
          })}
        </List>
      )}
    </Stack>
  )
}

export default ApplicantIncomeSection
